import React from 'react';
import PropTypes from 'prop-types';
import dynamic from 'next/dynamic';
import { makeStyles } from 'src/makeStyles';
import { get, flatten } from 'lodash';
import { Grid, Divider } from '@mui/material';
import { getComposerNamesFromCreators } from 'utils/composer';
import route, { companiesPaths } from '../../constants/routes';
import { useTranslation } from '../../src/i18n';
import { TP, ENTITY_TYPE } from '../../constants/consts';
import {
  getRouteWithCountryAndSlug,
  getDynamicRoutePropsWithAction,
  isCustomProductionName,
  getDatesInSameMonth,
  getDateWithDaysFormat,
} from '../../utils/common';
import { useCompaniesAndFestivalsData } from '../../utils/productionDataHelper';
import ViewDetailsLink from '../ViewDetailsLink';
import ExpandedCard from '../ExpandedCard';
import PerformanceCastContent from '../PerformanceCastContent';
import WorkInfoBlock from '../ProductionAccordionSummary/WorkInfoBlock';
import NextLink from '../NextLink';
import { colors, maskTextStyles, fontSize } from '../../constants/styleConstants';
import ImpressionTracker from '../../utils/components/impressionTracker';

// dynamic imports
const Title = dynamic(() => import('../Title'));

const useStyles = makeStyles()(theme => ({
  contentContainer: {
    width: '100%',
  },
  root: {
    display: 'flex',
    [theme.breakpoints.down('md')]: {
      flexDirection: 'column',
    },
  },
  dates: {
    width: '45%',
    fontSize: fontSize.basic,
    color: colors.darkText,
    fontWeight: 'bold',
    opacity: 0.5,
  },
  includesInfo: {
    marginTop: '1rem',
    marginBottom: '1rem',
    textAlign: 'left',
  },
  viewlink: {
    padding: '13px 0',
  },
  crewWrapper: {
    marginBottom: '1rem',
  },
  workInfo: {
    display: 'inline-block',
    '&::after': {
      content: "'|'",
      margin: '0 5px',
      color: colors.subTitle,
    },
  },
  maskOtherText: {
    textShadow: 'rgb(0 103 197 / 40%) 0px 0px 5px',
    ...maskTextStyles,
    '&:hover': {
      cursor: 'pointer',
    },
  },
}));

const RentalCard = ({ rental: production }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const rentalId = production?.rentals?.[0]?.id;
  const rentalIncludes = production?.rentals?.[0]?.rentalIncludes;
  const includedLists = rentalIncludes
    ?.map(item => item?.rentalIncludeType && item?.rentalIncludeType?.name)
    .filter(Boolean)
    .join(', ');
  const isProductionDatesInSameMonth =
    new Date(production?.minDate).getMonth() === new Date(production?.maxDate).getMonth() &&
    new Date(production?.minDate).getFullYear() === new Date(production?.maxDate).getFullYear();

  const productionDates = isProductionDatesInSameMonth
    ? getDatesInSameMonth(production?.minDate, production?.maxDate)
    : getDateWithDaysFormat(production?.minDate, production?.maxDate);

  const companiesAndFestivals = flatten(useCompaniesAndFestivalsData(production));
  const productionLinkProps = getDynamicRoutePropsWithAction(route.PRODUCTIONS, production);
  const isCustomWorkTitlePresent = isCustomProductionName(production);

  const castContent = (production?.casts?.length > 0 || production?.crew?.length > 0) && (
    <div>
      <div className={classes.title}>{t(`${TP}.FULL_PRODUCTION_CAST_CREW`)}</div>
      <PerformanceCastContent production={production} />
    </div>
  );

  const hasCustomWorkTitle = linkProps => (
    <NextLink {...linkProps} stopPropagation>
      <Title size="big" weight="bold">
        {production?.name}
      </Title>
    </NextLink>
  );

  const collapsedContent = (
    <>
      <Divider />
      <>
        {includedLists && (
          <div className={classes.includesInfo}>
            {t(`${TP}.m_EDIT_ROSTER_LIST_INCLUDE_SECTION_TITLE`)}: <strong>{includedLists}</strong>
          </div>
        )}
        <div className={classes.crewWrapper}>{castContent}</div>
      </>
      <Divider />
      <ViewDetailsLink
        className={classes.viewlink}
        linkText={t(`${TP}.FN_VIEW_PRODUCTION_DETAILS`)}
        linkProps={{
          href: `${route.PRODUCTIONS}/[id]`,
          as: `${route.PRODUCTIONS}/${get(production, 'slug')}-${get(production, 'id')}`,
        }}
      />
    </>
  );

  return (
    <ImpressionTracker
      data={{
        entityId: production?.id,
        entityName: production?.name,
        rentalId,
        rentalIncludedList: includedLists,
      }}
      entity={ENTITY_TYPE.PRODUCTION}
      eventType="rentalCardImpression"
      className={classes.contentContainer}
    >
      <ExpandedCard
        content={
          <>
            <div className={classes.root}>
              <Grid container>
                <Grid className={classes.dates} sm={4} md={2} item>
                  {' '}
                  {productionDates}
                </Grid>
                <Grid item sm={10} md={8}>
                  {hasCustomWorkTitle(productionLinkProps)}
                  {(isCustomWorkTitlePresent || production?.works?.length > 0) && (
                    <span className={classes.separatedContent}>
                      {production?.works?.map(item => (
                        <WorkInfoBlock
                          key={`${item.id}-${item.name}`}
                          production={production?.name}
                          work={item}
                          composerNames={getComposerNamesFromCreators(item?.creators)}
                          isMultiWorks={(production?.works || [])?.length > 0}
                          linkProps={isCustomWorkTitlePresent ? null : productionLinkProps}
                          hasCustomName={production?.name?.length > 0 || production?.production?.name?.length > 0}
                        />
                      ))}
                    </span>
                  )}
                  <div>
                    {companiesAndFestivals?.length > 0 && (
                      <span className={classes.separatedContent}>
                        {companiesAndFestivals?.map((item, index) => (
                          <span
                            key={`${item?.name}-${item?.id}`}
                            className={cx(item?.id === -1 && classes.maskOtherText)}
                          >
                            {index > 0 && ` \u00A0 | \u00A0`}
                            {item?.id === -1 ? (
                              <span>{item?.name}</span>
                            ) : (
                              <NextLink
                                href={companiesPaths.company}
                                as={getRouteWithCountryAndSlug(route.COMPANIES, item)}
                                className={cx(item?.id === -1 && classes.maskOtherText)}
                              >
                                {item.name}
                              </NextLink>
                            )}
                          </span>
                        ))}
                      </span>
                    )}
                  </div>
                </Grid>
              </Grid>
            </div>
          </>
        }
        collapsedContent={collapsedContent}
        contentClassName={classes.contentContainer}
      />
    </ImpressionTracker>
  );
};

RentalCard.propTypes = {
  rental: PropTypes.object,
};

export default RentalCard;
